import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Loader,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowsDiff,
  IconChevronDown,
  IconChevronUp,
  IconPlus,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAllProducts } from '@/Api';
import { clientRoutes } from '@/ApiPaths';
import { ProductListPreviewPicture } from '@/components/ProductListPreviewPicture';
import { PRODUCT_CARD_PREVIEW_BG } from '@/constants/productPreview';
import {
  MAX_COMPARE,
  toCompareSelectionItem,
  useCompareStore,
} from '@/store/compareStore';
import { useStore } from '@/store/Store';
import type { IProduct } from '@/types/Product.model';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import classes from './CompareTray.module.css';

function productSearchHaystack(product: IProduct): string {
  const tags = (product.tags ?? []).join(' ');
  return `${product.productName ?? ''} ${product.articleNumber ?? ''} ${tags}`.toLowerCase();
}

export function CompareTray() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const items = useCompareStore((s) => s.items);
  const minimized = useCompareStore((s) => s.minimized);
  const add = useCompareStore((s) => s.add);
  const remove = useCompareStore((s) => s.remove);
  const clear = useCompareStore((s) => s.clear);
  const setMinimized = useCompareStore((s) => s.setMinimized);
  const accentColor = useStore((s) => s.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;

  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [catalog, setCatalog] = useState<IProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const trayRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hideOnComparePage = location.pathname === clientRoutes.compare;
  const selectedIds = useMemo(() => items.map((i) => i._id), [items]);
  const emptySlots = Math.max(0, MAX_COMPARE - items.length);
  const trayVisible = items.length > 0 && !hideOnComparePage;

  useEffect(() => {
    // The full catalog is only needed for slot search, not merely for showing the tray.
    if (!trayVisible || !searchOpen || minimized || catalog.length > 0) return;
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(false);
    getAllProducts()
      .then((products) => {
        if (cancelled) return;
        setCatalog(products.filter((p) => Boolean(p._id)));
      })
      .catch(() => {
        if (!cancelled) {
          setCatalog([]);
          setCatalogError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trayVisible, searchOpen, minimized, catalog.length]);

  useEffect(() => {
    if (!searchOpen || minimized) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [searchOpen, minimized]);

  useEffect(() => {
    if (!searchOpen || minimized) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!trayRef.current?.contains(event.target as Node)) {
        setSearchOpen(false);
        setQuery('');
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [searchOpen, minimized]);

  useEffect(() => {
    if (minimized) {
      setSearchOpen(false);
      setQuery('');
    }
  }, [minimized]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? catalog.filter((product) => productSearchHaystack(product).includes(q))
      : catalog;
    return list
      .filter((p) => !selectedIds.includes(p._id))
      .slice(0, 50)
      .sort((a, b) => (a.productName || '').localeCompare(b.productName || ''));
  }, [catalog, query, selectedIds]);

  if (!trayVisible) return null;

  const goCompare = () => {
    const ids = items.map((i) => i._id).join(',');
    navigate(`${clientRoutes.compare}?ids=${encodeURIComponent(ids)}`);
  };

  const openSearch = () => {
    setMinimized(false);
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setQuery('');
  };

  const addProduct = (product: IProduct) => {
    const item = toCompareSelectionItem(product);
    if (!item) return;
    add(item);
    setQuery('');
    if (useCompareStore.getState().items.length >= MAX_COMPARE) {
      closeSearch();
    }
  };

  const countLabel = t('marketplace.compare.trayPeek', {
    count: items.length,
    max: MAX_COMPARE,
  });

  if (minimized) {
    return (
      <div className={classes.shell}>
        <UnstyledButton
          className={`${classes.pill} ${classes.pillEnter}`}
          onClick={() => setMinimized(false)}
          aria-label={countLabel}
          aria-expanded={false}
        >
          <IconArrowsDiff size={16} stroke={1.75} />
          <span>{countLabel}</span>
          <IconChevronUp size={16} stroke={1.75} />
        </UnstyledButton>
      </div>
    );
  }

  return (
    <Box
      ref={trayRef}
      className={`${classes.tray} ${classes.trayEnter}`}
      style={{
        borderTopColor: colorWithAlpha(accentColor, 0.35),
        background: 'color-mix(in srgb, var(--mantine-color-body) 92%, transparent)',
      }}
      role="region"
      aria-label={t('marketplace.compare.trayLabel')}
      aria-expanded={true}
    >
      {searchOpen ? (
        <div className={classes.searchPanel}>
          <div className={classes.searchPanelHeader}>
            <TextInput
              ref={inputRef}
              className={classes.searchPanelInput}
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder={t('marketplace.compare.searchPlaceholder')}
              leftSection={<IconSearch size={16} />}
              rightSection={
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="gray"
                  aria-label={t('common.close')}
                  onClick={closeSearch}
                >
                  <IconX size={14} />
                </ActionIcon>
              }
            />
          </div>
          <div className={classes.searchPanelBody}>
            {catalogLoading ? (
              <div className={classes.searchStatus}>
                <Loader size="sm" color={accentColor} />
                <Text size="sm" c="dimmed" ml="sm">
                  {t('marketplace.compare.loading')}
                </Text>
              </div>
            ) : catalogError ? (
              <Text size="sm" c="red" p="sm">
                {t('marketplace.compare.loadError')}
              </Text>
            ) : filtered.length === 0 ? (
              <Text size="sm" c="dimmed" p="sm">
                {t('marketplace.compare.noResults')}
              </Text>
            ) : (
              <ScrollArea h={280} type="auto" offsetScrollbars>
                <div className={classes.searchList}>
                  {filtered.map((product) => (
                    <UnstyledButton
                      key={product._id}
                      className={classes.searchRow}
                      onClick={() => addProduct(product)}
                    >
                      <Box className={classes.thumb} style={{ background: PRODUCT_CARD_PREVIEW_BG }}>
                        {product.productImage ? (
                          <ProductListPreviewPicture
                            entityId={product._id}
                            productImage={product.productImage}
                            imageVersion={product.imageVersion}
                            alt={product.productName}
                          />
                        ) : null}
                      </Box>
                      <div className={classes.meta}>
                        <Text fw={600} size="sm" lineClamp={1}>
                          {product.productName}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {product.articleNumber || '—'}
                        </Text>
                      </div>
                      <Button
                        size="compact-xs"
                        variant="light"
                        color={accentColor}
                        leftSection={<IconPlus size={12} />}
                        onClick={(event: MouseEvent<HTMLButtonElement>) => {
                          event.stopPropagation();
                          addProduct(product);
                        }}
                      >
                        {t('marketplace.compare.add')}
                      </Button>
                    </UnstyledButton>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      ) : null}

      <div className={classes.inner}>
        <div className={classes.slots}>
          {items.map((item) => (
            <div key={item._id} className={classes.slot}>
              <ActionIcon
                className={classes.removeBtn}
                size="xs"
                variant="subtle"
                color="gray"
                aria-label={t('marketplace.compare.remove')}
                onClick={() => remove(item._id)}
              >
                <IconX size={12} />
              </ActionIcon>
              <Box className={classes.thumb} style={{ background: PRODUCT_CARD_PREVIEW_BG }}>
                {item.productImage ? (
                  <ProductListPreviewPicture
                    entityId={item._id}
                    productImage={item.productImage}
                    imageVersion={item.imageVersion}
                    alt={item.productName}
                  />
                ) : null}
              </Box>
              <div className={classes.meta}>
                <Text fw={600} size="sm" lineClamp={1}>
                  {item.productName}
                </Text>
                {item.articleNumber ? (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {item.articleNumber}
                  </Text>
                ) : null}
              </div>
            </div>
          ))}

          {Array.from({ length: emptySlots }).map((_, index) => (
            <UnstyledButton
              key={`empty-${index}`}
              className={`${classes.slot} ${classes.slotEmpty}`}
              onClick={openSearch}
              aria-label={t('marketplace.compare.traySearch')}
            >
              <Text size="xs" c="dimmed" className={classes.emptyHint} lineClamp={2}>
                {t('marketplace.compare.trayEmptyHint')}
              </Text>
              <span className={classes.emptySearch}>
                <IconSearch size={14} stroke={1.75} />
                <span>{t('marketplace.compare.traySearch')}</span>
              </span>
            </UnstyledButton>
          ))}
        </div>

        <Group gap="sm" wrap="nowrap" className={classes.actions}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={t('marketplace.compare.minimize')}
            onClick={() => setMinimized(true)}
          >
            <IconChevronDown size={18} stroke={1.75} />
          </ActionIcon>
          <Button
            color={accentColor}
            leftSection={<IconArrowsDiff size={16} />}
            onClick={goCompare}
            disabled={items.length < 1}
          >
            {t('marketplace.compare.compareAction')}
          </Button>
          <UnstyledButton className={classes.clearLink} onClick={() => clear()}>
            <IconX size={14} />
            <span>{t('marketplace.compare.clearAll')}</span>
          </UnstyledButton>
        </Group>
      </div>
    </Box>
  );
}
